import { LoadingOverlay, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  InventoryItemForm,
  InventoryItemFormErrors,
} from 'src/Components/Forms/Inventory/InventoryItemForm';
import { ChosenInvoice } from 'src/Components/Inventory/InventoryInvoices';
import { InventoryFormValues } from 'src/Components/Inventory/InventoryItemFields';
import { useInventoryRooms } from 'src/Components/Inventory/useInventoryRooms';
import { FormAttachment } from 'src/Hooks/Forms/types/formAttachment.types';
import { useUpdateInventoryItem } from 'src/Hooks/Mutations/useInventoryMutations';
import useFetchInventory, { useFetchInventoryItem } from 'src/Hooks/Queries/useFetchInventory';
import { AssetStorageItemDtoTypeEnum } from 'src/Services/API/generated';

import { Props } from './InventoryItemEditionFormWrapper.types';

/** Edit an object: same form as the creation, with its photos and invoices. */
const InventoryItemEditionFormWrapper = ({ itemId, onClose }: Props) => {
  const { t } = useTranslation();
  const { data: item, isLoading } = useFetchInventoryItem(itemId);
  const { data: inventory } = useFetchInventory();
  const { roomOptions } = useInventoryRooms();
  const { mutate: update, isLoading: isSaving } = useUpdateInventoryItem(itemId);

  const [values, setValues] = useState<InventoryFormValues | null>(null);
  const [photos, setPhotos] = useState<FormAttachment[]>([]);
  const [photosUploading, setPhotosUploading] = useState(false);
  const [invoices, setInvoices] = useState<ChosenInvoice[]>([]);
  const [errors, setErrors] = useState<InventoryItemFormErrors>({});

  useEffect(() => {
    if (!item || values) return;
    setValues({
      name: item.name,
      roomId: item.room?.id ?? null,
      brandAndModel: item.brandAndModel ?? '',
      condition: item.condition ?? '',
      notes: item.notes ?? '',
      quantity: item.quantity,
      purchaseYear: item.purchaseYear ?? null,
      purchasePrice: item.purchasePrice ?? null,
      replacementValue: item.replacementValue ?? null,
      replacementValueSource: item.replacementValueSource ?? null,
      replacementValueSourceLabel: item.replacementValueSourceLabel ?? null,
      replacementValueSourceUrl: item.replacementValueSourceUrl ?? null,
      depreciationEnabled: item.depreciationEnabled,
    });
    // Photos added before and photos from Documents alike: the id is the one to send back.
    setPhotos(
      item.photos.map(photo => ({
        fileId: photo.id,
        name: photo.originalName,
        previewUrl: photo.previewThumbnailUrl ?? photo.previewMediumUrl ?? photo.url,
        type: AssetStorageItemDtoTypeEnum.File,
      }))
    );
    setInvoices(
      item.invoices.map(invoice => ({ id: invoice.id, name: invoice.name, url: invoice.url }))
    );
  }, [item, values]);

  if (isLoading) return <LoadingOverlay visible />;
  if (!item) return <Text>{t('inventory.item.error', 'Impossible de retrouver cet objet.')}</Text>;
  if (!values) return <LoadingOverlay visible />;

  const valuesVisible = item.valuesVisible;

  const save = () => {
    const nextErrors: InventoryItemFormErrors = {};
    if (!values.name.trim()) {
      nextErrors.name = t(
        'inventory.add.nameRequired',
        "Donnez un nom à l'objet (ex. « Canapé »)."
      );
    }
    if (!photos.length) {
      nextErrors.photos = t('inventory.add.photoRequired', 'Prenez au moins une photo de l’objet.');
    } else if (photosUploading) {
      nextErrors.photos = t(
        'inventory.add.photosPending',
        'Attendez que les photos soient envoyées.'
      );
    }
    setErrors(nextErrors);
    if (nextErrors.name || nextErrors.photos) return;

    update(
      {
        name: values.name.trim(),
        roomId: values.roomId,
        brandAndModel: values.brandAndModel || null,
        condition: values.condition || null,
        notes: values.notes || null,
        quantity: values.quantity,
        purchaseYear: values.purchaseYear,
        depreciationEnabled: values.depreciationEnabled,
        ...(valuesVisible && {
          purchasePrice: values.purchasePrice,
          replacementValue: values.replacementValue,
          replacementValueSource: values.replacementValueSource,
          replacementValueSourceLabel: values.replacementValueSourceLabel,
          replacementValueSourceUrl: values.replacementValueSourceUrl,
        }),
        photoIds: photos.flatMap(photo => photo.fileId ?? []),
        invoiceFileIds: invoices.map(invoice => invoice.id),
      },
      {
        onSuccess: () => {
          notifications.show({
            color: 'teal',
            message: t('inventory.edit.saved', 'Fiche enregistrée.'),
          });
          onClose();
        },
        onError: () =>
          setErrors({
            submit: t('inventory.edit.error', "La fiche n'a pas pu être enregistrée. Réessayez."),
          }),
      }
    );
  };

  return (
    <InventoryItemForm
      values={values}
      onChange={patch => setValues(current => (current ? { ...current, ...patch } : current))}
      photos={photos}
      onPhotosChange={setPhotos}
      onPhotosUploadingChange={setPhotosUploading}
      invoices={invoices}
      onInvoicesChange={setInvoices}
      roomOptions={roomOptions}
      valuesVisible={valuesVisible}
      rule={inventory?.settings ?? { depreciationRatePercent: 10, depreciationMaxPercent: 80 }}
      errors={errors}
      onSubmit={save}
      onCancel={onClose}
      isLoading={isSaving}
      submitLabel={t('common.save', 'Enregistrer')}
    />
  );
};

export default InventoryItemEditionFormWrapper;
